<template>
  <div class="mt-4">
    <v-card class="mx-auto" max-width="95%" elevation="2">
      <v-toolbar dark dense color="#37474F">
        <v-toolbar-title>
          <v-icon left>mdi-flask-outline</v-icon>
          포인트 채굴기 · API 테스터
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-chip small outlined dark>{{ tests.length }} 테스트</v-chip>
      </v-toolbar>

      <v-row no-gutters>
        <!-- 좌측: 테스트 목록 -->
        <v-col cols="12" md="3" class="pa-3 grey lighten-5" style="border-right:1px solid #eee; min-height:420px">
          <v-list dense>
            <v-list-item
              v-for="t in tests"
              :key="t.key"
              @click="selectTest(t.key)"
              :class="{ 'primary lighten-5': selected === t.key }"
            >
              <v-list-item-icon class="mr-2">
                <v-icon :color="selected === t.key ? 'primary' : 'grey'">{{ t.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">{{ t.label }}</v-list-item-title>
                <v-list-item-subtitle class="text-caption">
                  <code style="font-size:11px">{{ t.endpoint }}</code>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-col>

        <!-- 우측: 폼 + 실행 + 응답 -->
        <v-col cols="12" md="9" class="pa-4">
          <!-- ── 유저 푸시 발송 ── -->
          <div v-if="selected === 'push'">
            <div class="text-h6 mb-1">
              <v-icon left color="#37474F">mdi-cellphone-message</v-icon>
              특정 유저에게 푸시 발송
            </div>
            <div class="text-caption grey--text mb-4">
              <code>POST /api/cpu/send_push_by_userid</code>
              — CMS 가 유저 토큰을 조회해 Redis 푸시 풀(<code>Friend_Save_Push</code>)에 등록하면,
              service_api_v1 의 크론(Cron_Push_Sender_Live)이 5초 이내 FCM 으로 전송합니다.
            </div>

            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="push.user_id"
                  label="유저 아이디 *"
                  outlined dense
                  prepend-inner-icon="mdi-account"
                  placeholder="예: test_user_123"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="push.title"
                  label="푸시 제목"
                  outlined dense
                  prepend-inner-icon="mdi-format-title"
                  placeholder="새로운 소식!"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="push.message"
                  label="푸시 내용 *"
                  outlined dense rows="3"
                  prepend-inner-icon="mdi-message-text"
                  placeholder="테스트 푸시 메시지입니다."
                  hide-details="auto"
                ></v-textarea>
              </v-col>
            </v-row>

            <div class="mt-3">
              <v-btn
                color="primary" depressed
                :loading="calling"
                :disabled="!push.user_id || !push.message"
                @click="sendPush"
              >
                <v-icon left small>mdi-send</v-icon>푸시 발송
              </v-btn>
              <v-btn text class="ml-1" @click="resetResponse" :disabled="!responseText">응답 비움</v-btn>
            </div>
          </div>

          <!-- ── 오퍼월 테스트 지급 ── -->
          <div v-if="selected === 'offerwall'">
            <div class="text-h6 mb-1">
              <v-icon left color="#37474F">mdi-cash-multiple</v-icon>
              오퍼월 테스트 지급
            </div>
            <div class="text-caption grey--text mb-4">
              <code>POST /api/cpu/offerwall_test</code>
              — 선택한 오퍼월의 샘플 포스트백을 <strong>대상 도메인</strong>의
              <code>/pub/offerwall_receive_&lt;provider&gt;</code> 로 전송합니다. 라이브 서버에서도 동작합니다.
            </div>

            <!-- 대상 서버(도메인) 설정 -->
            <v-card outlined class="mb-4 pa-3" color="blue-grey lighten-5">
              <div class="d-flex align-center mb-2">
                <v-icon small left color="blue-grey">mdi-server-network</v-icon>
                <span class="text-subtitle-2 font-weight-bold">대상 서버 도메인</span>
                <v-spacer></v-spacer>
                <v-btn-toggle v-model="offerwall.env" mandatory dense color="primary">
                  <v-btn value="live" small><v-icon left small>mdi-broadcast</v-icon>라이브</v-btn>
                  <v-btn value="dev" small><v-icon left small>mdi-hammer-wrench</v-icon>개발</v-btn>
                </v-btn-toggle>
              </div>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.trim="offerwall.domain_live"
                    label="라이브 도메인"
                    outlined dense
                    prepend-inner-icon="mdi-broadcast"
                    placeholder="https://cpuapi.snapplay.io"
                    :background-color="offerwall.env === 'live' ? 'white' : undefined"
                    hint="한번 입력하면 로컬(localStorage)에 저장되어 유지됩니다."
                    persistent-hint
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model.trim="offerwall.domain_dev"
                    label="개발 도메인"
                    outlined dense
                    prepend-inner-icon="mdi-hammer-wrench"
                    placeholder="https://cpudev.snapplay.io"
                    :background-color="offerwall.env === 'dev' ? 'white' : undefined"
                    hint="바꾸기 전까지 유지됩니다."
                    persistent-hint
                  ></v-text-field>
                </v-col>
              </v-row>
              <div class="text-caption mt-2">
                <v-icon x-small>mdi-send</v-icon>
                전송 대상:
                <code>{{ offerwallTargetUrlPreview }}</code>
                <span v-if="!offerwallBaseDomain" class="red--text ml-1">← 도메인을 입력하세요</span>
              </div>
            </v-card>

            <v-row dense>
              <v-col cols="12">
                <v-select
                  v-model="offerwall.provider"
                  :items="offerwall_provider_options"
                  item-text="label"
                  item-value="value"
                  label="오퍼월 종류 *"
                  outlined dense
                  prepend-inner-icon="mdi-storefront"
                  hint="선택한 오퍼월로 샘플 포스트백을 전송합니다."
                  persistent-hint
                ></v-select>
              </v-col>
            </v-row>

            <!-- Snapplay 전용: 최근 수집 adv_id -->
            <v-row dense v-if="offerwall.provider === 'snapplay'">
              <v-col cols="12">
                <v-select
                  v-model="offerwall.adv_id"
                  :items="offerwall_adv_options"
                  label="adv_id (최근 수집)"
                  outlined dense clearable
                  :loading="offerwall_adv_loading"
                  prepend-inner-icon="mdi-bullhorn"
                  :hint="offerwall_adv_target_dt ? ('수집일 ' + offerwall_adv_target_dt + ' · ' + offerwall_adv_options.length + '개') : '비우면 기본값(1001)으로 전송'"
                  persistent-hint
                >
                  <template v-slot:append-outer>
                    <v-btn icon small :loading="offerwall_adv_loading" @click="loadAdvIds" title="adv_id 새로고침">
                      <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                  </template>
                </v-select>
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="offerwall.user_id"
                  label="유저 아이디 *"
                  outlined dense
                  prepend-inner-icon="mdi-account"
                  placeholder="예: test_user_123"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="offerwall.user_pnt"
                  label="지급 포인트 (user_pnt) *"
                  type="number"
                  outlined dense
                  prepend-inner-icon="mdi-cash"
                  placeholder="예: 100"
                  hide-details="auto"
                ></v-text-field>
              </v-col>
            </v-row>

            <div class="mt-3">
              <v-btn
                color="primary" depressed
                :loading="calling"
                :disabled="!isOfferwallValid"
                @click="sendOfferwall"
              >
                <v-icon left small>mdi-send</v-icon>오퍼월 테스트 지급
              </v-btn>
              <v-btn text class="ml-1" @click="resetResponse" :disabled="!responseText">응답 비움</v-btn>
            </div>
          </div>

          <!-- 응답 (공통) -->
          <v-card outlined class="mt-4">
            <v-card-title class="text-subtitle-2 grey lighten-4 pa-2">
              <v-icon left small>mdi-import</v-icon>응답
              <v-spacer></v-spacer>
              <span v-if="responseOk !== null">
                <v-chip x-small :color="responseOk ? 'green' : 'red'" dark>
                  {{ responseOk ? '성공' : '실패' }}
                </v-chip>
                <span class="ml-2 grey--text text-caption">{{ responseTime }}ms</span>
              </span>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pa-0">
              <pre v-if="responseText" style="margin:0; padding:12px; background:#f5f5f5; font-size:12px; max-height:360px; overflow:auto">{{ responseText }}</pre>
              <div v-else class="pa-6 text-center grey--text text-caption">아직 호출하지 않았습니다.</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000" top>
      {{ snackbarText }}
    </v-snackbar>
    <div class="py-8"></div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

// 오퍼월 도메인 저장 키 (localStorage) — 바꾸기 전까지 유지
const LS_DOMAIN_LIVE = 'cpu_offerwall_domain_live';
const LS_DOMAIN_DEV = 'cpu_offerwall_domain_dev';
const LS_ENV = 'cpu_offerwall_env';

// 각 오퍼월의 게임서버 수신 경로 — 전송 대상 URL 미리보기용 (백엔드 매핑과 동일)
const OFFERWALL_PATHS = {
  snapplay: '/pub/offerwall_receive_snapplay',
  adjo: '/pub/offerwall_receive_adjo',
  cpx_aos: '/pub/offerwall_receive_cpx_aos',
  cpx_ios: '/pub/offerwall_receive_cpx_ios',
  dt: '/pub/offerwall_receive_dt',
  gmo: '/pub/offerwall_receive_gmo',
  house: '/pub/offerwall_receive_house',
  maf: '/pub/offerwall_receive_maf',
  mallpie: '/pub/offerwall_receive_mallpie',
  nas_aos: '/pub/offerwall_receive_nas_aos',
  nas_ios: '/pub/offerwall_receive_nas_ios',
  pincrux: '/pub/offerwall_receive_pincrux',
  tnk: '/pub/offerwall_receive_tnk',
  tyrads: '/pub/offerwall_receive_tyrads',
};

export default {
  name: 'CPU_ApiTester',

  data: () => ({
    selected: 'push',
    tests: [
      { key: 'push', label: '유저 푸시 발송', icon: 'mdi-cellphone-message', endpoint: '/api/cpu/send_push_by_userid' },
      { key: 'offerwall', label: '오퍼월 테스트 지급', icon: 'mdi-cash-multiple', endpoint: '/api/cpu/offerwall_test' },
    ],
    push: {
      user_id: '',
      title: '',
      message: '',
    },
    // 오퍼월 테스트
    offerwall: {
      env: 'dev',                              // 'live' | 'dev'
      domain_live: 'https://cpuapi.snapplay.io',
      domain_dev: 'https://cpudev.snapplay.io',
      provider: 'snapplay',
      user_id: '',
      user_pnt: '',
      adv_id: '',
    },
    offerwall_adv_options: [],
    offerwall_adv_target_dt: null,
    offerwall_adv_loading: false,
    offerwall_provider_options: [
      { value: 'snapplay', label: 'Snapplay' },
      { value: 'adjo', label: 'ADJO' },
      { value: 'cpx_aos', label: 'CPX (AOS)' },
      { value: 'cpx_ios', label: 'CPX (iOS)' },
      { value: 'dt', label: 'DT' },
      { value: 'gmo', label: 'GMO' },
      { value: 'house', label: 'House(자체)' },
      { value: 'maf', label: 'MAF' },
      { value: 'mallpie', label: 'Mallpie' },
      { value: 'nas_aos', label: 'NSTATION (AOS)' },
      { value: 'nas_ios', label: 'NSTATION (iOS)' },
      { value: 'pincrux', label: 'Pincrux' },
      { value: 'tnk', label: 'TNK' },
      { value: 'tyrads', label: 'TyrAds' },
    ],
    calling: false,
    responseText: '',
    responseOk: null,
    responseTime: 0,
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
  }),

  computed: {
    // 현재 선택된 환경(라이브/개발)의 도메인 — 끝 슬래시 정리
    offerwallBaseDomain() {
      const raw = this.offerwall.env === 'live' ? this.offerwall.domain_live : this.offerwall.domain_dev;
      return (raw || '').trim().replace(/\/+$/, '');
    },
    offerwallTargetUrlPreview() {
      const path = OFFERWALL_PATHS[this.offerwall.provider] || '/pub/offerwall_receive_...';
      return (this.offerwallBaseDomain || '(도메인 미입력)') + path;
    },
    isOfferwallValid() {
      return !!this.offerwall.provider &&
             !!this.offerwall.user_id &&
             !!this.offerwall.user_pnt &&
             Number(this.offerwall.user_pnt) > 0 &&
             /^https?:\/\//i.test(this.offerwallBaseDomain);
    },
  },

  created() {
    // 저장해둔 도메인/환경 복원 (바꾸기 전까지 유지)
    const live = localStorage.getItem(LS_DOMAIN_LIVE);
    const dev = localStorage.getItem(LS_DOMAIN_DEV);
    const env = localStorage.getItem(LS_ENV);
    if (live) this.offerwall.domain_live = live;
    if (dev) this.offerwall.domain_dev = dev;
    if (env === 'live' || env === 'dev') this.offerwall.env = env;
  },

  watch: {
    // 도메인/환경 변경 시 즉시 localStorage 에 저장
    'offerwall.domain_live'(v) { localStorage.setItem(LS_DOMAIN_LIVE, (v || '').trim()); },
    'offerwall.domain_dev'(v) { localStorage.setItem(LS_DOMAIN_DEV, (v || '').trim()); },
    'offerwall.env'(v) { localStorage.setItem(LS_ENV, v); },
    // 스냅오퍼월 선택 시 최근 adv_id 자동 로드
    'offerwall.provider': {
      immediate: true,
      handler(v) {
        if (v === 'snapplay' && this.offerwall_adv_options.length === 0 && !this.offerwall_adv_loading) {
          this.loadAdvIds();
        }
      },
    },
  },

  methods: {
    ...mapActions(['CPU_SEND_PUSH_BY_USERID', 'CPU_OFFERWALL_TEST', 'CPU_GET_RECENT_ADV_IDS']),

    selectTest(key) {
      this.selected = key;
      this.resetResponse();
    },

    resetResponse() {
      this.responseText = '';
      this.responseOk = null;
      this.responseTime = 0;
    },

    showSnackbar(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color || 'success';
      this.snackbar = true;
    },

    async sendPush() {
      if (!this.push.user_id || !this.push.message) {
        this.showSnackbar('유저 아이디와 푸시 내용은 필수입니다.', 'error');
        return;
      }
      this.calling = true;
      this.resetResponse();
      const t0 = Date.now();
      try {
        const result = await this.CPU_SEND_PUSH_BY_USERID({
          user_id: this.push.user_id,
          title: this.push.title,
          message: this.push.message,
        });
        this.responseOk = true;
        this.responseText = JSON.stringify(result, null, 2);
        this.showSnackbar('푸시 풀에 등록되었습니다. 잠시 후 전송됩니다.', 'success');
      } catch (err) {
        this.responseOk = false;
        this.responseText = typeof err === 'string' ? err : JSON.stringify(err, null, 2);
        this.showSnackbar('푸시 등록 실패: ' + (typeof err === 'string' ? err : '오류'), 'error');
      } finally {
        this.responseTime = Date.now() - t0;
        this.calling = false;
      }
    },

    async loadAdvIds() {
      this.offerwall_adv_loading = true;
      try {
        const r = await this.CPU_GET_RECENT_ADV_IDS();
        const list = (r && Array.isArray(r.adv_ids)) ? r.adv_ids : [];
        this.offerwall_adv_options = list.map(String);
        this.offerwall_adv_target_dt = (r && r.target_dt) ? String(r.target_dt).slice(0, 10) : null;
        if (this.offerwall_adv_options.length && !this.offerwall_adv_options.includes(this.offerwall.adv_id)) {
          this.offerwall.adv_id = this.offerwall_adv_options[0];
        }
      } catch (e) {
        // adv_id 목록이 없어도 오퍼월 테스트는 기본값(1001)으로 동작하므로 조용히 무시
        console.error('adv_id 목록 로드 실패:', e);
      } finally {
        this.offerwall_adv_loading = false;
      }
    },

    async sendOfferwall() {
      if (!this.isOfferwallValid) {
        this.showSnackbar('오퍼월 종류·유저 아이디·포인트·도메인을 확인해주세요.', 'error');
        return;
      }
      this.calling = true;
      this.resetResponse();
      const t0 = Date.now();
      try {
        const result = await this.CPU_OFFERWALL_TEST({
          provider: this.offerwall.provider,
          user_id: this.offerwall.user_id,
          user_pnt: Number(this.offerwall.user_pnt),
          adv_id: this.offerwall.provider === 'snapplay' ? this.offerwall.adv_id : undefined,
          base_domain: this.offerwallBaseDomain,
        });
        this.responseOk = true;
        this.responseText = JSON.stringify(result, null, 2);
        this.showSnackbar('오퍼월 테스트 포스트백을 전송했습니다.', 'success');
      } catch (err) {
        this.responseOk = false;
        this.responseText = typeof err === 'string' ? err : JSON.stringify(err, null, 2);
        this.showSnackbar('오퍼월 테스트 실패: ' + (typeof err === 'string' ? err : '오류'), 'error');
      } finally {
        this.responseTime = Date.now() - t0;
        this.calling = false;
      }
    },
  },
};
</script>
